//@ts-nocheck
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { assignReviewTemplateDetails, fetchReviewData, fetchReviewDataByJob, fetchReviewDataStatus, fetchReviewDetails, updateReviewData } from '../../services/reviewService';
import { ApiError, CreateReviewPayload, ReviewResponse, TeamsState, updateTeamsPayload } from './reviewTypes';
import { createReviewData } from '../../services/reviewService';



export const fetchReviewList = createAsyncThunk<
  ReviewResponse,
  number,
  { rejectValue: string }
>(
  'base/fetchReviewList',
  async (baseId, { rejectWithValue }) => {
    try {
      if (baseId === undefined) {
        throw new Error('Base ID is undefined');
      }
      const response = await fetchReviewData(baseId);
      return response;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue(error.message || 'Failed to fetch review data');
    }
  }
);


// fetch location by status
export const fetchReviewListStatus = createAsyncThunk<
  ReviewResponse,
  { baseId: number; status?: string },
  { rejectValue: string }
>(
  'base/fetchReviewListStatus',
  async ({ baseId, status }, { rejectWithValue }) => {
    try {
      const response = await fetchReviewDataStatus(baseId, status);
      return response;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue(error.message || 'Failed to fetch review data');
    }
  }
);


// create teams 
export const createReview = createAsyncThunk(
  'base/createReview',
  async ({ payload, userId }: { payload: CreateReviewPayload; userId: number }, { rejectWithValue }) => {
    try {
      const response = await createReviewData(payload, userId);
      return response;
    } catch (err) {
      const error = err as ApiError;
      // Handling specific API error for duplicate emails
      if (error.response?.status === 409) {
        const customErrorMessage = error.response?.data?.error || 'Review already exists with the same base_id';
        return rejectWithValue(customErrorMessage);  // Return the custom error message for 409 conflict
      }

      // Handle other errors
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Failed to create review';
      return rejectWithValue(errorMessage);  // Return generic error message
    }
  }
);



export const updateReview = createAsyncThunk<ReviewResponse, { payload: updateTeamsPayload; userId: number, id: number },
  { rejectValue: string }
>(
  'base/updateReview',
  async ({ payload, userId, id }, { rejectWithValue }) => {
    try {
      const response = await updateReviewData(userId, id, payload);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(error.response?.data?.message || 'Failed to update review');
    }
  }
);


export const fetchReviewDetailsData = createAsyncThunk<
  ReviewResponse,
  { baseId: number; userId: string },
  { rejectValue: string }
>(
  'base/fetchReviewDetails',
  async ({ baseId, userId }, { rejectWithValue }) => {
    try {
      const response = await fetchReviewDetails(baseId, userId);
      return response;  // Ensure this matches ReviewResponse type
    } catch (err) {
      const error = err as Error;
      return rejectWithValue(error.message || 'Failed to fetch review details');
    }
  }
);

// fetch location by status
export const fetchReviewListByJobId = createAsyncThunk<
  ReviewResponse,
  { baseId: number; status?: string, jobId?: string },
  { rejectValue: string }
>(
  'base/fetchReviewListByJobId',
  async ({ baseId, status, jobId }, { rejectWithValue }) => {
    try {
      const response = await fetchReviewDataByJob(baseId, status, jobId);
      return response;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue(error.message || 'Failed to fetch review data');
    }
  }
);


export const assignReviewTemplateData = createAsyncThunk<
  ReviewResponse,
  { baseId: number; userId: string; JobId: string; },
  { rejectValue: string }
>(
  'base/assignReviewTemplateData',
  async ({ baseId, userId, JobId }, { rejectWithValue }) => {
    try {
      if (baseId === undefined) {
        throw new Error('Base ID is undefined');
      }
      const response = await assignReviewTemplateDetails(baseId, userId, JobId);
      return response;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue(error.response.data.data.message || 'Failed to fetch score card details');
    }
  }
);



const initialState: TeamsState = {
  locationDetails: null,
  locationStatus: 'idle',
  locationStatusError: null,
  createTeamsStatus: 'idle',
  createTeamsError: null,
  createTeamsData: null,
  updateTeamsStatus: 'idle',
  updateTeamsError: null,
  updateTeamsData: null,
  deleteTeamsStatus: 'idle',
  deleteTeamsError: null,
  fetchTeamsByIdStatus: 'idle',
  fetchTeamsByIdError: null,
  fetchTeamsByIdData: null,
  baseMemberInviteStatus: 'idle',
  baseMemberInviteError: null,
  baseMemberInviteData: null,
  locationDetails1: null,
  locationStatus1: 'idle',
  locationStatusError1: null,
  // fetch pipeline details

  pipelineDetails: null,
  pipelineStatus: 'idle',
  pipelineStatusError: null,

  fetchReviewByJobIdStatus: 'idle',
  fetchReviewByJobIdError: null,
  fetchReviewByJobIdData: null,
  // assign job pipeline details
  assignJobPipelineDetails: null,
  assignJobPipelineStatus: 'idle',
  assignJobPipelineStatusError: null


};

const reviewSlice = createSlice({
  name: 'review',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReviewList.pending, (state) => {
        state.locationStatus = 'loading';
      })
      .addCase(fetchReviewList.fulfilled, (state, action: PayloadAction<ReviewResponse>) => {
        state.locationStatus = 'succeeded';
        state.locationDetails = action.payload.data;
        // localStorage.setItem('basePiplineData', JSON.stringify(action.payload));
      })
      .addCase(fetchReviewList.rejected, (state, action) => {
        state.locationStatus = 'failed';
        state.locationStatusError = action.payload || 'Failed to fetch review';
      })
      .addCase(createReview.pending, (state) => {
        state.createTeamsStatus = 'loading';
      })
      .addCase(createReview.fulfilled, (state, action: PayloadAction<ReviewResponse>) => {
        state.createTeamsStatus = 'succeeded';
        state.createTeamsData = action.payload;
      })
      .addCase(createReview.rejected, (state, action) => {
        state.createTeamsStatus = 'failed';
        state.createTeamsError = action.payload || 'Failed to create review';
      })
      .addCase(updateReview.pending, (state) => {
        state.updateTeamsStatus = 'loading';
      })
      .addCase(updateReview.fulfilled, (state, action: PayloadAction<ReviewResponse>) => {
        state.updateTeamsStatus = 'succeeded';
        state.updateTeamsData = action.payload;
      })
      .addCase(updateReview.rejected, (state, action) => {
        state.updateTeamsStatus = 'failed';
        state.updateTeamsError = action.payload || 'Failed to create review';
      })
      .addCase(fetchReviewListStatus.pending, (state) => {
        state.locationStatus1 = 'loading';
      })
      .addCase(fetchReviewListStatus.fulfilled, (state, action: PayloadAction<ReviewResponse>) => {
        state.locationStatus1 = 'succeeded';
        state.locationDetails1 = action.payload.data;
        // localStorage.setItem('basePiplineData', JSON.stringify(action.payload));
      })
      .addCase(fetchReviewListStatus.rejected, (state, action) => {
        state.locationStatus1 = 'failed';
        state.locationStatusError1 = action.payload || 'Failed to fetch review';
      })
      .addCase(fetchReviewDetailsData.pending, (state) => {
        state.pipelineStatus = 'loading';
      })
      .addCase(fetchReviewDetailsData.fulfilled, (state, action: PayloadAction<ReviewResponse>) => {
        state.pipelineStatus = 'succeeded';
        state.pipelineDetails = action.payload.data;
        // localStorage.setItem('basePiplineData', JSON.stringify(action.payload));
      })
      .addCase(fetchReviewDetailsData.rejected, (state, action) => {
        state.pipelineStatus = 'failed';
        state.pipelineStatusError = action.payload || 'Failed to fetch review';
      })
      .addCase(fetchReviewListByJobId.pending, (state) => {
        state.fetchReviewByJobIdStatus = 'loading';
      })
      .addCase(fetchReviewListByJobId.fulfilled, (state, action: PayloadAction<ReviewResponse>) => {
        state.fetchReviewByJobIdStatus = 'succeeded';
        state.fetchReviewByJobIdData = action.payload.data;
        // localStorage.setItem('basePiplineData', JSON.stringify(action.payload));
      })
      .addCase(fetchReviewListByJobId.rejected, (state, action) => {
        state.fetchReviewByJobIdStatus = 'failed';
        state.fetchReviewByJobIdError = action.payload || 'Failed to fetch review';
      })
      .addCase(assignReviewTemplateData.pending, (state) => {
        state.assignJobPipelineStatus = 'loading';
      })
      .addCase(assignReviewTemplateData.fulfilled, (state, action: PayloadAction<ReviewResponse>) => {
        state.assignJobPipelineStatus = 'succeeded';
        state.assignJobPipelineDetails = action.payload.data;
        // localStorage.setItem('basePiplineData', JSON.stringify(action.payload));
      })
      .addCase(assignReviewTemplateData.rejected, (state, action) => {
        state.assignJobPipelineStatus = 'failed';
        state.assignJobPipelineStatusError = action.payload || 'Failed to fetch review';
      })

  }
});


export default reviewSlice.reducer;

